/* eslint-disable max-lines */
import { LocalDate } from '@js-joda/core';
import { compact, noop, toInteger } from 'lodash';
import { useMemo } from 'react';

import { DoctorSimple } from '@dreryk/egabinet-personnel-frontend-api';
import { PractitionerId } from '@dreryk/egabinet-personnel-published-language-api';
import { VisitAndAppointmentView, VisitAndAppointmentViewQuery } from '@dreryk/egabinet-visit-frontend-api';

import {
   LazyLoadedDataConfigurationByCqrsQuery,
   useLazyLoadedDataConfigurationByCqrsQuery,
} from '@dreryk/drerykweb-main-frontend';
import {
   convertDateToLocalDate,
   dateTimeMinuteFormatter,
   Ordering,
   patientNameFormatter,
   useTranslate,
} from '@dreryk/react-common';
import {
   ColumnWidth,
   createDynamicColumnsWidth,
   HorizontalAlign,
   JustifyContent,
   Padding,
   TableColumnConfiguration,
   TextPlaceholder,
} from '@dreryk/react-components';
import { Slot } from '@dreryk/watermelon';

import { protectFeature } from '~libs.shared.system';
import { AppointmentNoteCell } from '~libs.desktop.shared.appointment';
import { PractitionerSafePresenter } from '~libs.desktop.shared.practitioner-safe-presenter';
import { visitPaths } from '~libs.desktop.shared.paths';
import { PatientEntitlementToMedicalServiceStatusIcon } from '~libs.desktop.patient-entitlement-to-medical-service';
import { PatientGroups } from '~libs.desktop.shared.patient-groups';
import { PatientTableCellPresenter } from '~libs.desktop.shared.patient';

import { appointmentsTranslationKeys } from '../../translations';
import { RefetchAppointments } from '../appointments.types';
import { useAppointmentsFiltersContext } from '../filters/AppointmentFilter.context';
import { getAppointmentConfirmationQueryValue } from '../filters/AppointmentsFilterPanel/fields/AppointmentConfirmationStatusField/AppointmentConfirmationStatusField.utils';

import { AdditionalOptionsCell } from './cells/AdditionalOptions/AdditionalOptionsCell';
import { DiseaseItemList } from './cells/DiseaseItemList/DiseaseItemList';
import { MedicalEventCell } from './cells/MedicalEventCell';
import { OptionsCell } from './cells/OptionsCell/OptionsCell';
import { PreInterviewCell } from './cells/PreInterviewCell/PreInterviewCell';
import { StatusCell } from './cells/StatusCell';
import { VisitSourceCell } from './cells/VisitSourceCell';
import { AppointmentConfirmationStatus as AppointmentConfirmationStatusComponent } from './cells/AppointmentConfirmationStatus/AppointmentConfirmationStatus';
import { PatientPresenceStatus } from './cells/PatientPresenceStatus/PatientPresenceStatus';
import { EncounterModeCell } from './cells/EncounterModeCell';

const isAppointmentStatusRegistered = (status: VisitAndAppointmentView['status']): boolean => status === 'REGISTERED';

const getPatientLastNameColumnWidth = (medicalDataAvailable: boolean, doctorsFilterAvailable: boolean): ColumnWidth => {
   if (medicalDataAvailable && doctorsFilterAvailable) {
      return '33%';
   }
   if (medicalDataAvailable && !doctorsFilterAvailable) {
      return '50%';
   }
   if (!medicalDataAvailable && doctorsFilterAvailable) {
      return '50%';
   }
   return '100%';
};
const getDiagnosesColumnWidth = (medicalDataAvailable: boolean, doctorsFilterAvailable: boolean): ColumnWidth => {
   if (medicalDataAvailable && doctorsFilterAvailable) {
      return '33%';
   }
   if (medicalDataAvailable && !doctorsFilterAvailable) {
      return '50%';
   }
   return '0px';
};
const getPractitionerColumnWidth = (medicalDataAvailable: boolean, doctorsFilterAvailable: boolean): ColumnWidth => {
   if (medicalDataAvailable && doctorsFilterAvailable) {
      return '34%';
   }
   if (!medicalDataAvailable && doctorsFilterAvailable) {
      return '50%';
   }
   return '0px';
};

// eslint-disable-next-line max-lines-per-function
export const useAppointmentsTableColumns = (
   refetchAppointments: RefetchAppointments,
   doctorsFilterAvailable: boolean
): TableColumnConfiguration<VisitAndAppointmentView>[] => {
   const translate = useTranslate();
   const searchingMedicalDataAvailable = protectFeature('searchingMedicalData');
   const conductingPreInterviewAvailable = protectFeature('conductingPreInterview');

   // eslint-disable-next-line max-lines-per-function
   return useMemo(() => {
      const columnsWidth = createDynamicColumnsWidth({
         beginDate: '140px',
         patientGroups: '22px',
         patientLastName: getPatientLastNameColumnWidth(searchingMedicalDataAvailable, doctorsFilterAvailable),
         practitioner: getPractitionerColumnWidth(searchingMedicalDataAvailable, doctorsFilterAvailable),
         entitlementToMedicalService: '52px',
         note: '32px',
         diagnoses: getDiagnosesColumnWidth(searchingMedicalDataAvailable, doctorsFilterAvailable),
         status: '158px',
         sourceLabel: '22px',
         encounterMode: '32px',
         appointmentConfirmationStatus: '32px',
         patientPresenceStatus: '32px',
         medicalEvents: '52px',
         preInterview: conductingPreInterviewAvailable ? '123px' : '0px',
         options: '122px',
         additionalOptions: '24px',
      });

      return compact([
         {
            accessor: 'beginDate',
            label: appointmentsTranslationKeys.datetime,
            width: columnsWidth.beginDate,
            formatter: dateTimeMinuteFormatter,
         },
         {
            name: 'patientGroups',
            accessor: 'patientId',
            width: columnsWidth.patientGroups,
            sortingDisabled: true,
            justifyContent: JustifyContent.CENTER,
            padding: Padding.VERTICAL,
            renderer: (props) => {
               return <PatientGroups patientGroups={props.bean.patientGroups} direction={'column'} />;
            },
         },
         {
            name: 'patientLastName',
            accessor: 'patientId',
            label: appointmentsTranslationKeys.patient,
            width: columnsWidth.patientLastName,

            renderer: (props) => {
               return <PatientTableCellPresenter patient={props.bean} />;
            },
         },
         doctorsFilterAvailable && {
            name: 'practitioner',
            accessor: 'practitioner',
            label: appointmentsTranslationKeys.specialist,
            width: columnsWidth.practitioner,

            renderer: (props) => {
               return (
                  <PractitionerSafePresenter
                     practitioner={props.bean.practitioner}
                     testId={'author'}
                     presentationOrder={'lastName'}
                     type={'standalone'}
                     withIdentification
                  />
               );
            },
         },
         {
            name: 'entitlementToMedicalService',
            accessor: 'entitlementToMedicalService',
            padding: Padding.NONE,
            label: appointmentsTranslationKeys.entitlementToMedicalService,
            width: columnsWidth.entitlementToMedicalService,

            renderer: ({ bean }) => (
               <PatientEntitlementToMedicalServiceStatusIcon
                  patientId={bean.patientId}
                  visitId={bean.visitId}
                  entitlementToMedicalService={bean.entitlementToMedicalService}
                  checkDate={bean.beginDate}
                  patientReferenceIdentifier={bean.patientReferenceIdentifier}
                  readonly={!isAppointmentStatusRegistered(bean.status)}
               />
            ),
            sortingDisabled: true,
            justifyContent: JustifyContent.CENTER,
            textAlign: HorizontalAlign.CENTER,
         },
         {
            name: 'note',
            accessor: noop,
            width: columnsWidth.note,
            padding: Padding.NONE,
            justifyContent: JustifyContent.CENTER,
            sortingDisabled: true,
            renderer: ({ bean }) =>
               bean.note ? (
                  <AppointmentNoteCell
                     appointmentId={bean.appointmentId}
                     note={bean.note}
                     onNoteUpdated={refetchAppointments}
                  />
               ) : null,
         },
         searchingMedicalDataAvailable && {
            name: 'diagnoses',
            accessor: 'diagnoses',
            label: appointmentsTranslationKeys.diagnosis,
            width: columnsWidth.diagnoses,

            renderer: ({ bean }) => (bean.diagnoses.length ? <DiseaseItemList diagnoses={bean.diagnoses} /> : null),
            sortingDisabled: true,
            placeholder: TextPlaceholder.NO_DATA,
         },
         {
            name: 'status',
            accessor: 'status',
            label: appointmentsTranslationKeys.status,
            width: columnsWidth.status,

            renderer: ({ bean }) => {
               return bean.status ? <StatusCell status={bean.status} /> : null;
            },
         },
         {
            name: 'sourceLabel',
            accessor: 'sourceLabel',
            width: columnsWidth.sourceLabel,
            sortingDisabled: true,
            padding: Padding.NONE,
            justifyContent: JustifyContent.CENTER,
            renderer: ({ bean }) => {
               return <VisitSourceCell sourceLabel={bean.sourceLabel} />;
            },
         },
         {
            name: 'encounterMode',
            accessor: 'encounterMode',
            width: columnsWidth.encounterMode,
            sortingDisabled: true,
            padding: Padding.NONE,
            justifyContent: JustifyContent.CENTER,
            renderer: ({ bean }) => {
               return <EncounterModeCell encounterMode={bean.encounterMode} />;
            },
         },
         {
            name: 'confirmed',
            accessor: 'confirmed',
            width: columnsWidth.appointmentConfirmationStatus,
            sortingDisabled: true,
            padding: Padding.NONE,
            justifyContent: JustifyContent.CENTER,
            renderer: ({ bean }) => {
               if (!bean.timeSlotConfirmed) {
                  return null;
               }
               return (
                  <AppointmentConfirmationStatusComponent
                     confirmed={bean.confirmed}
                     appointmentId={bean.appointmentId}
                     disabled={bean.status !== 'REGISTERED'}
                  />
               );
            },
         },
         {
            name: 'patientPresence',
            accessor: 'patientPresence',
            width: columnsWidth.patientPresenceStatus,
            sortingDisabled: true,
            padding: Padding.NONE,
            justifyContent: JustifyContent.CENTER,
            renderer: ({ bean }) => {
               if (!bean.timeSlotConfirmed) {
                  return null;
               }
               return (
                  <PatientPresenceStatus patientPresence={bean.patientPresence} appointmentId={bean.appointmentId} />
               );
            },
         },
         {
            name: 'medicalEvents',
            accessor: noop,
            label: appointmentsTranslationKeys.medicalEvents,
            width: columnsWidth.medicalEvents,
            padding: Padding.NONE,
            renderer: ({ bean }) => <MedicalEventCell visitMedicalEventStatus={bean.visitMedicalEventStatus} />,
            justifyContent: JustifyContent.CENTER,
            textAlign: HorizontalAlign.CENTER,
         },
         conductingPreInterviewAvailable && {
            name: 'preInterview',
            accessor: noop,
            label: appointmentsTranslationKeys.preInterview.table.column.title,
            sortingDisabled: true,
            width: columnsWidth.preInterview,

            renderer: ({ bean }) => {
               const patientFullName =
                  patientNameFormatter(
                     {
                        firstName: bean.patientFirstName,
                        lastName: bean.patientLastName,
                        phoneNumber: bean.patientPhoneNumber,
                     },
                     { options: { order: 'firstLast' }, translator: translate }
                  ) ?? '';

               return (
                  <PreInterviewCell
                     appointmentId={bean.appointmentId}
                     beginDate={bean.beginDate}
                     endDate={bean.endDate}
                     patientFullName={patientFullName}
                     patientId={bean.patientId}
                     visitId={bean.visitId}
                     visitStatus={bean.status}
                  />
               );
            },
         },
         {
            name: 'options',
            accessor: noop,
            label: appointmentsTranslationKeys.options,
            width: columnsWidth.options,

            renderer: (props) => {
               const visitBeginDate = convertDateToLocalDate(props.bean.beginDate).atStartOfDay();
               const today = LocalDate.now().atStartOfDay();
               return (
                  <OptionsCell
                     isFutureVisit={visitBeginDate.isAfter(today)}
                     visitAndAppointmentView={props.bean}
                     refetchAppointments={refetchAppointments}
                     ownedByCurrentUser={props.bean.ownedByCurrentUser}
                  />
               );
            },
            sortingDisabled: true,
         },
         {
            name: 'additionalOptions',
            width: columnsWidth.additionalOptions,
            accessor: noop,
            padding: Padding.NONE,
            renderer: ({ bean }) => {
               return bean.appointmentId ? (
                  <AdditionalOptionsCell {...bean} refetchAppointments={refetchAppointments} />
               ) : null;
            },
            sortingDisabled: true,
         },
      ]);
   }, [
      doctorsFilterAvailable,
      searchingMedicalDataAvailable,
      conductingPreInterviewAvailable,
      refetchAppointments,
      translate,
   ]);
};

export const useAppointmentsDataConfiguration = (
   currentDoctor: DoctorSimple | null,
   doctors: DoctorSimple[]
): LazyLoadedDataConfigurationByCqrsQuery<VisitAndAppointmentView> => {
   const { getFilter } = useAppointmentsFiltersContext();
   const dateFilter = getFilter('date');
   const practitionerIds = getFilter('practitioner');
   const visitAndAppointmentStatuses = getFilter('visitAndAppointmentStatus');
   const visitEncounterModes = getFilter('visitEncounterMode');
   const appointmentConfirmationStatuses = getFilter('appointmentConfirmationStatus');
   const patientPresences = getFilter('patientPresence');
   const patientGroupsIds = getFilter('patientGroup');

   const orderings: Ordering<VisitAndAppointmentView>[] = useMemo(
      () => [
         {
            property: 'beginDate',
            descending: !dateFilter,
         },
      ],
      [dateFilter]
   );

   let practitionerIdsQueryValue: PractitionerId[] = [];
   if (practitionerIds.length) {
      practitionerIdsQueryValue = practitionerIds.map(Number).map((id) => PractitionerId.of(id));
   }
   if (currentDoctor && !practitionerIds.length) {
      practitionerIdsQueryValue = [PractitionerId.of(currentDoctor.practitionerId)];
   }
   if (!currentDoctor && doctors.length && !practitionerIds.length) {
      practitionerIdsQueryValue = doctors.map(({ practitionerId }) => PractitionerId.of(practitionerId));
   }

   return useLazyLoadedDataConfigurationByCqrsQuery<VisitAndAppointmentView>(() => {
      const query = new VisitAndAppointmentViewQuery();
      const { patientId } = visitPaths.useAppointmentsListSearchParams();
      query.patientId = Slot.emptyIfNull(patientId ? toInteger(patientId) : null);
      query.appointmentDateRange = dateFilter?.range ?? undefined;
      query.practitionerIds = Slot.of(practitionerIdsQueryValue);
      query.statuses = Slot.emptyIfNull(visitAndAppointmentStatuses.length ? visitAndAppointmentStatuses : null);
      query.encounterModes = Slot.emptyIfNull(visitEncounterModes.length ? visitEncounterModes : null);
      query.patientPresences = Slot.emptyIfNull(patientPresences.length ? patientPresences : null);
      query.patientGroupsIds = Slot.emptyIfNull(patientGroupsIds.length ? patientGroupsIds : null);
      const confirmationStatus =
         appointmentConfirmationStatuses.length === 1
            ? getAppointmentConfirmationQueryValue(appointmentConfirmationStatuses.at(0)!)
            : null;
      query.confirmed = Slot.emptyIfNull(confirmationStatus);

      return query;
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, orderings);
};
